import { Injectable } from "@angular/core";
import { NavigationExtras, Router, UrlTree } from "@angular/router";
import { Store } from "@ngrx/store";
import { combineLatest, Observable } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";
import * as fromAuth from "src/app/store/actions/auth.actions";
import * as fromAuthStore from "src/app/store/reducers/auth.reducer";
import * as fromAccountStore from "src/app/store/reducers/account.reducer";
import { Member } from "../../interfaces/member";
import { ApiService } from "../api/api.service";
import { RegisterRequest } from "../../interfaces/register-request";
import { Guid } from "../../types/guid";
import { NameRequest } from "../../interfaces/name-request";
import { PasswordRequest } from "../../interfaces/password-request";
import { ForgotPasswordRequest } from "../../interfaces/forgot-password-request";
import { ResetPasswordRequest } from "../../interfaces/reset-password-request";
import { EmailRequest } from "../../interfaces/email-request";
import { ErrorListing } from "../../types/error-listing";
import { LimitType } from "../../types/limit-type";
import { CourseLimit } from "../../interfaces/limit";
import { ActivationCode } from "../../interfaces/activation-code";
import {
  LeagueRegulationsPayload,
  RegulationsPayload,
} from "../../interfaces/regulations-payload";

@Injectable({
  providedIn: "root",
})
export class AccountService {
  constructor(
    private apiService: ApiService,
    private router: Router,
    private store: Store<{
      account: fromAccountStore.AccountState;
      auth: fromAuthStore.AuthState;
    }>,
  ) {}

  getMember(): Observable<Member> {
    return this.apiService.getRequest<Member>("me/");
  }

  getAuthenticatedMember(): Observable<Member | null> {
    return this.authGuardState().pipe(map(([member]) => member));
  }

  checkIsLoggedIn(): Observable<boolean> {
    return this.authGuardState().pipe(map(([member]) => member !== null));
  }

  createLoginUrlTree(): UrlTree {
    return this.router.createUrlTree(["/account/login"]);
  }

  createMemberHomeUrlTree(member: Member): UrlTree {
    return this.router.createUrlTree(this.getMemberHomeCommands(member));
  }

  private authGuardState(): Observable<[Member | null, boolean, boolean]> {
    return combineLatest([
      this.store.select((state) => state.account.member),
      this.store.select((state) => state.account.getMemberFailed),
      this.store.select((state) => state.auth.readTokenFailed),
    ]).pipe(
      tap(([member, getMemberFailed, readTokenFailed]) => {
        if (member === null && !getMemberFailed && !readTokenFailed) {
          this.store.dispatch({ type: fromAuth.READ_TOKEN });
        }
      }),
      filter(
        ([member, getMemberFailed, readTokenFailed]) =>
          member !== null || getMemberFailed || readTokenFailed,
      ),
      take(1),
    );
  }

  redirectMember(
    member: Member,
    redeemCode: Guid,
  ): Promise<boolean> | undefined {
    if (
      !["account/login", "account/redeem"].some((route) =>
        this.router.url.includes(route),
      )
    ) {
      return;
    }

    const extras: NavigationExtras = redeemCode
      ? { queryParams: { redeemCode } }
      : {};

    return this.router.navigate(this.getMemberHomeCommands(member), extras);
  }

  private getMemberHomeCommands(member: Member): string[] {
    if (member.is_orgadmin) {
      return ["/orgadmin"];
    }

    if (member.is_teacher) {
      return ["/teacher"];
    }

    return ["/student"];
  }

  registerTeacher(params: {
    request: RegisterRequest;
    invitationId?: Guid;
    redeemCode?: Guid;
  }): Observable<{}> {
    return this.apiService.postRequest<{}>(
      `register/teacher/${params.invitationId || params.redeemCode}/`,
      params.request,
    );
  }

  registerStudent(params: {
    request: RegisterRequest;
    invitationId?: Guid;
    redeemCode?: Guid;
  }): Observable<{}> {
    return this.apiService.postRequest<{}>(
      `register/student/${params.invitationId || params.redeemCode}/`,
      params.request,
    );
  }

  precheckTeacher(params: {
    invitationId: Guid;
  }): Observable<{ email: string }> {
    return this.apiService.getRequest<{ email: string }>(
      `register/teacher/${params.invitationId}/precheck/`,
    );
  }

  precheckStudent(params: {
    invitationId: Guid;
  }): Observable<{ email: string }> {
    return this.apiService.getRequest<{ email: string }>(
      `register/student/${params.invitationId}/precheck/`,
    );
  }

  changeName(params: NameRequest): Observable<Member> {
    return this.apiService.putRequest<Member>(`me/name/`, params);
  }

  changePassword(params: PasswordRequest): Observable<{}> {
    return this.apiService.putRequest<{}>(`me/password/`, params);
  }

  forgotPassword(params: ForgotPasswordRequest): Observable<{}> {
    return this.apiService.postRequest<{}>(`password/forgot/`, params);
  }

  resetPassword(params: {
    request: ResetPasswordRequest;
    forgotToken: Guid;
  }): Observable<{}> {
    return this.apiService.putRequest<{}>(
      `password/reset/${params.forgotToken}/`,
      params.request,
    );
  }

  changeEmail(params: EmailRequest): Observable<{} | ErrorListing> {
    return this.apiService.putRequest<{} | ErrorListing>(`me/email/`, params);
  }

  changeEmailCheck(emailToken: Guid): Observable<{}> {
    return this.apiService.putRequest<{}>(`me/email/${emailToken}/`, {});
  }

  updateInRanking(settingsBody: { in_ranking: boolean }): Observable<Member> {
    return this.apiService.putRequest<Member>(`me/in_ranking/`, settingsBody);
  }

  redeem(code: Guid): Observable<any> {
    return this.apiService.putRequest("coderedeem/activate/", { code });
  }

  getLimits(type: LimitType, courseId?: Guid): Observable<Array<CourseLimit>> {
    const url = courseId
      ? `me/limits/courses/${courseId}/`
      : `me/limits/courses/`;

    return this.apiService.getRequest<Array<CourseLimit>>(url);
  }

  getLimit(
    type: LimitType,
    id: Guid,
    organizationId?: string,
  ): Observable<CourseLimit> {
    let url = `me/limits/courses/${id}/`;

    if (organizationId) {
      url += `?organization=${organizationId}`;
    }
    return this.apiService.getRequest<CourseLimit>(url);
  }

  terms(payload: RegulationsPayload): Observable<{ logout_url: string }> {
    return this.apiService.putRequest<{ logout_url: string }>(
      "me/terms/",
      payload,
    );
  }

  checkActivationCode(CodeId: string): Observable<ActivationCode> {
    return this.apiService.getRequest<ActivationCode>(
      `activation_code/${CodeId}/`,
    );
  }

  sendInvitationFromQR(
    CodeId: string,
    email: string,
    accept_terms: boolean,
    language: string,
  ): Observable<void> {
    return this.apiService.postRequest<void>(`activation_code/${CodeId}/`, {
      email,
      accept_terms,
      language,
    });
  }

  leagueTerms(payload: LeagueRegulationsPayload): Observable<void> {
    return this.apiService.putRequest<void>("me/league-terms/", payload);
  }
}
